/**
 * The theory note on one topic or sub-topic, in the curriculum console.
 *
 * Upload is a JSON file read in the browser and sent as the request body — it
 * is a structured document a few kilobytes long, not a file to store, and the
 * server validates every field before anything is saved. A rejected file comes
 * back with the fields that are wrong, named the way they appear in the file.
 *
 * The preview is the exam's own renderer, so what an author checks here is
 * exactly what a student opens at "Read Theory".
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiDelete, apiErrorMessage, apiGet, apiPut } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import {
  THEORY_NOTE_MAX_BYTES,
  THEORY_NOTE_TEMPLATE,
  type TheoryNoteDTO,
  type TheoryNoteLevel,
} from '@cwy/config/contracts';
import { Button, Card } from '@/components/ds';
import { TheoryNoteView } from '@/components/theory/TheoryNoteView';

/** Hand the browser a JSON document to save. */
function download(fileName: string, value: unknown) {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

const slugOf = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'theory';

export function TheoryNotePanel({
  level,
  nodeId,
  nodeName,
}: {
  level: TheoryNoteLevel;
  nodeId: string;
  nodeName: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  const queryKey = ['curriculum', 'theory', level, nodeId];

  const note = useQuery({
    queryKey,
    queryFn: () =>
      apiGet<TheoryNoteDTO | null>(`/curriculum/theory?level=${level}&nodeId=${nodeId}`),
  });

  const upload = useMutation({
    mutationFn: (input: { fileName: string; content: unknown }) =>
      apiPut<TheoryNoteDTO>('/curriculum/theory', { level, nodeId, ...input }),
    onSuccess: (saved) => {
      setError('');
      queryClient.setQueryData(queryKey, saved);
      setPreview(true);
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => apiDelete(`/curriculum/theory?level=${level}&nodeId=${nodeId}`),
    onSuccess: () => {
      setError('');
      queryClient.setQueryData(queryKey, null);
      setPreview(false);
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    if (file.size > THEORY_NOTE_MAX_BYTES) {
      setError(
        `That file is ${Math.ceil(file.size / 1024)} KB — a theory note can be at most ${THEORY_NOTE_MAX_BYTES / 1024} KB.`,
      );
      return;
    }
    let content: unknown;
    try {
      content = JSON.parse(await file.text());
    } catch {
      setError(`${file.name} is not valid JSON. Check for a missing comma or quote.`);
      return;
    }
    upload.mutate({ fileName: file.name, content });
  };

  const current = note.data ?? null;
  const what = level === 'CHAPTER' ? 'topic' : 'sub-topic';

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
            Theory note · {nodeName}
          </h3>
          <p className="mt-0.5 text-xs" style={{ color: 'var(--ink-3)' }}>
            {note.isPending
              ? 'Loading…'
              : current
                ? `${current.fileName ?? 'Uploaded'} · updated ${new Date(current.updatedAt).toLocaleString()}`
                : level === 'SUBTOPIC'
                  ? 'No note yet. Students see this topic’s note instead, if it has one.'
                  : 'No note yet. Students see the one-line theory reference until one is uploaded.'}
          </p>
          <p className="mt-1 text-xs" style={{ color: 'var(--ink-3)' }}>
            Students open it from “Read Theory” once a question on this {what} unlocks its third
            attempt.
          </p>
        </div>

        <div className="flex flex-none flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => {
              void onFile(event.target.files?.[0]);
              // So choosing the same file again (after fixing it) fires again.
              event.target.value = '';
            }}
          />
          <Button size="sm" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
            {upload.isPending ? 'Checking…' : current ? 'Replace JSON' : 'Upload JSON'}
          </Button>
          {current && (
            <>
              <Button size="sm" variant="secondary" onClick={() => setPreview((v) => !v)}>
                {preview ? 'Hide preview' : 'Preview'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  download(current.fileName ?? `${slugOf(nodeName)}.json`, current.content)
                }
              >
                Download
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Remove the theory note on “${nodeName}”?`)) remove.mutate();
                }}
              >
                Remove
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => download('theory-note-template.json', THEORY_NOTE_TEMPLATE)}
          >
            Template
          </Button>
        </div>
      </div>

      {error && (
        <p className="mt-3 whitespace-pre-line text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      {preview && current && (
        <div
          className="mt-4 max-w-2xl rounded-xl p-4"
          style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}
        >
          <TheoryNoteView note={current.content} />
        </div>
      )}
    </Card>
  );
}
